import { useState, useRef, useCallback } from 'react';

export function useMediaDevices() {
	const [stream, setStream] = useState(null);
	const [audioMuted, setAudioMuted] = useState(false);
	const [videoDisabled, setVideoDisabled] = useState(false);
	const [error, setError] = useState(null);
	const streamRef = useRef(null);

	const getStream = useCallback(async ({ audio = true, video = false } = {}) => {
		setError(null);
		try {
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				audio: audio
					? { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
					: false,
				video: video ? { width: 1280, height: 720, facingMode: 'user' } : false,
			});
			streamRef.current = mediaStream;
			setStream(mediaStream);
			setAudioMuted(false);
			setVideoDisabled(false);
			return mediaStream;
		} catch (err) {
			console.error('useMediaDevices: getUserMedia error:', err);
			setError(err.message || 'Cannot access camera/microphone');
			return null;
		}
	}, []);

	const toggleAudio = useCallback(() => {
		const s = streamRef.current;
		if (!s) return;
		const track = s.getAudioTracks()[0];
		if (!track) return;
		track.enabled = !track.enabled;
		setAudioMuted(!track.enabled);
	}, []);

	const toggleVideo = useCallback(() => {
		const s = streamRef.current;
		if (!s) return;
		const track = s.getVideoTracks()[0];
		if (!track) return;
		track.enabled = !track.enabled;
		setVideoDisabled(!track.enabled);
	}, []);

	const stopStream = useCallback(() => {
		const s = streamRef.current;
		if (s) {
			s.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
		setStream(null);
		setAudioMuted(false);
		setVideoDisabled(false);
	}, []);

	return {
		stream,
		streamRef,
		audioMuted,
		videoDisabled,
		error,
		getStream,
		toggleAudio,
		toggleVideo,
		stopStream,
	};
}
